<script setup lang="ts">
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import { computed } from 'vue';
import type { AvailabilityResponse } from '../api';
import ChosenCard from './ChosenCard.vue';

export type CalendarState = 'DateChosen' | 'DateNotChosen';

const props = defineProps<{
  state: CalendarState;
  availability: AvailabilityResponse | null;
  currentDate: string; // YYYY-MM-DD, pierwszy dzień miesiąca
  selectedDate: string | null; // YYYY-MM-DD
  selectedSlot: string | null; // pełny datetime wybranego slotu
  enabled: boolean; // czy można już wybierać datę (mamy zarówno user, jak i service)
  loadingAvailability: boolean;
}>();

const emit = defineEmits<{
  (e: 'change-month', direction: 'prev' | 'next'): void;
  (e: 'select-date', date: string): void;
  (e: 'select-slot', datetime: string): void;
  (e: 'toggle-display-mode'): void;
}>();

const monthLabel = computed(() => {
  const m = dayjs(props.currentDate || dayjs().startOf('month'));
  const monthNames = [
    'Styczeń',
    'Luty',
    'Marzec',
    'Kwiecień',
    'Maj',
    'Czerwiec',
    'Lipiec',
    'Sierpień',
    'Wrzesień',
    'Październik',
    'Listopad',
    'Grudzień',
  ];
  return `${monthNames[m.month()]} ${m.year()}`;
});

function buildGrid() {
  const base: Dayjs = dayjs(props.currentDate || dayjs().startOf('month')).startOf('month');
  const year = base.year();
  const monthIndex = base.month();

  const daysInMonth = base.daysInMonth();
  const firstDayOfMonth = base.day(); // 0=Niedz ... 6=Sob
  const offset = (firstDayOfMonth + 6) % 7; // Pon=0

  const slotsByDate = props.availability?.slots || {};
  const today = dayjs().startOf('day');

  const cells: Array<{
    key: number;
    label: string | null;
    date: string | null;
    isPast: boolean;
    hasSlots: boolean;
    isSelected: boolean;
    isToday: boolean;
  }> = [];

  for (let i = 0; i < 42; i++) {
    const dayNum = i - offset + 1;

    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push({
        key: i,
        label: null,
        date: null,
        isPast: false,
        hasSlots: false,
        isSelected: false,
        isToday: false,
      });
      continue;
    }

    const date = dayjs(`${year}-${monthIndex + 1}-${dayNum}`).format('YYYY-MM-DD');
    const hasSlots =
      Array.isArray(slotsByDate[date]) && (slotsByDate[date] as string[]).length > 0;
    const isPast = dayjs(date).isBefore(today, 'day');
    const isSelected = props.selectedDate === date;
    const isToday = dayjs(date).isSame(today, 'day');

    cells.push({
      key: i,
      label: String(dayNum),
      date,
      isPast,
      hasSlots,
      isSelected,
      isToday,
    });
  }

  return cells;
}

const cells = computed(() => buildGrid());

const timeSlotsForSelectedDate = computed(() => {
  if (!props.availability || !props.selectedDate) return [];
  const slots = props.availability.slots[props.selectedDate];
  return Array.isArray(slots) ? (slots as string[]) : [];
});

const hasAnyAvailableSlots = computed(() => {
  const slotsByDate = props.availability?.slots;
  if (!slotsByDate) {
    return false;
  }
  return Object.values(slotsByDate).some(
    (slots) => Array.isArray(slots) && slots.length > 0
  );
});

const showNoSlotsMessage = computed(
  () =>
    props.enabled &&
    !props.loadingAvailability &&
    props.availability !== null &&
    !hasAnyAvailableSlots.value
);

function handlePrev() {
  emit('change-month', 'prev');
}

function handleNext() {
  emit('change-month', 'next');
}

function handleSelect(cell: (typeof cells.value)[number]) {
  if (!cell.date || cell.isPast || !cell.hasSlots) return;
  emit('select-date', cell.date);
}

function handleSelectTime(time: string) {
  if (!props.selectedDate) return;
  emit('select-slot', `${props.selectedDate} ${time}`);
}
</script>

<template>
  <div class="mbw-section-with-separator">
    <div v-if="state === 'DateChosen'">
      <ChosenCard :first-line="dayjs(selectedDate).locale('pl').format('ddd, DD.MM.YYYY')"
        :second-line="selectedSlot ? selectedSlot.split(' ')[1] : ''" :muted-second-line="false"
        @change-click="emit('toggle-display-mode')">
        <template #icon>
          <div class="mbw-datechosen-icon">
            <div class="mbw-datechosen-icon-bg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2V6" stroke="#3C3D41" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16 2V6" stroke="#3C3D41" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path
                  d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
                  stroke="#3C3D41" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M3 10H21" stroke="#3C3D41" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M8 14H8.01" stroke="#3C3D41" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M12 14H12.01" stroke="#3C3D41" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M16 14H16.01" stroke="#3C3D41" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M8 18H8.01" stroke="#3C3D41" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M12 18H12.01" stroke="#3C3D41" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M16 18H16.01" stroke="#3C3D41" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
          </div>
        </template>
      </ChosenCard>
    </div>

    <div v-else class="mbw-calendar-wrapper">
      <div v-if="loadingAvailability" class="mbw-calendar-loading-state">
        <div class="mbw-calendar-loading-content">
          <div class="mbw-spinner"></div>
          <div class="mbw-calendar-loading-text">
            Ładujemy dostępne terminy...
          </div>
        </div>
      </div>

      <div v-else-if="showNoSlotsMessage" class="mbw-alert mbw-alert--info" role="alert">
        <div class="mbw-alert-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
            <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
        </div>
        <div class="mbw-alert-content">
          <div class="mbw-alert-title">Brak dostępnych terminów</div>
          <p class="mbw-alert-text">
            Spróbuj wybrać innego specjalistę lub inną usługę.
          </p>
        </div>
      </div>

      <template v-else-if="enabled">
        <div class="mbw-calendar-header">
          <button type="button" class="mbw-calendar-nav mbw-calendar-nav--prev" @click="handlePrev">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L1 7L7 1" stroke="#3C3D41" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
          <div class="mbw-calendar-title">{{ monthLabel }}</div>
          <button type="button" class="mbw-calendar-nav mbw-calendar-nav--next" @click="handleNext">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 13L7 7L1 1" stroke="#3C3D41" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <div class="mbw-calendar-weekdays">
          <div class="mbw-calendar-weekday">Pon</div>
          <div class="mbw-calendar-weekday">Wt</div>
          <div class="mbw-calendar-weekday">Śr</div>
          <div class="mbw-calendar-weekday">Czw</div>
          <div class="mbw-calendar-weekday">Pt</div>
          <div class="mbw-calendar-weekday">Sob</div>
          <div class="mbw-calendar-weekday">Niedz</div>
        </div>

        <div class="mbw-calendar-grid">
          <button v-for="cell in cells" :key="cell.key" type="button" class="mbw-calendar-cell" :class="{
            'mbw-calendar-cell--empty': !cell.date,
            'mbw-calendar-cell--past': cell.isPast,
            'mbw-calendar-cell--has-slots': cell.hasSlots,
            'mbw-calendar-cell--selected': cell.isSelected,
            'mbw-calendar-cell--today': cell.isToday,
          }" :disabled="!cell.date || cell.isPast || !cell.hasSlots" @click="handleSelect(cell)">
            <span v-if="cell.label" class="mbw-calendar-day-number">
              {{ cell.label }}
            </span>
          </button>
        </div>

        <!-- Wybór godziny w stanie edycji -->
        <div v-if="selectedDate && timeSlotsForSelectedDate.length" class="mbw-time-wrapper">
          <div class="mbw-time-label">
            Wybierz godzinę •
            {{ dayjs(selectedDate).format('DD.MM.YYYY') }}
          </div>

          <div class="mbw-mobile-overflow-icon" style="transform: translateY(0px);">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22" fill="none">
              <path
                d="M16.7501 15.4167C16.7501 16.8312 16.1882 18.1877 15.188 19.1879C14.1878 20.1881 12.8312 20.75 11.4167 20.75"
                stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path
                d="M14.0834 13.4167V12.75C14.0834 12.3964 13.9429 12.0573 13.6929 11.8072C13.4428 11.5572 13.1037 11.4167 12.7501 11.4167C12.3965 11.4167 12.0573 11.5572 11.8073 11.8072C11.5572 12.0573 11.4167 12.3964 11.4167 12.75"
                stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path
                d="M11.4167 12.75V12.0833C11.4167 11.7297 11.2762 11.3906 11.0261 11.1405C10.7761 10.8905 10.437 10.75 10.0833 10.75C9.72971 10.75 9.39057 10.8905 9.14053 11.1405C8.89048 11.3906 8.75 11.7297 8.75 12.0833V12.75"
                stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path
                d="M8.74992 12.4167V8.75002C8.74992 8.3964 8.60945 8.05726 8.3594 7.80721C8.10935 7.55716 7.77021 7.41669 7.41659 7.41669C7.06296 7.41669 6.72383 7.55716 6.47378 7.80721C6.22373 8.05726 6.08325 8.3964 6.08325 8.75002V15.4167"
                stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path
                d="M14.0833 13.4167C14.0833 13.0631 14.2237 12.7239 14.4738 12.4739C14.7238 12.2238 15.063 12.0833 15.4166 12.0833C15.7702 12.0833 16.1094 12.2238 16.3594 12.4739C16.6095 12.7239 16.7499 13.0631 16.7499 13.4167V15.4167C16.7499 16.8312 16.188 18.1877 15.1878 19.1879C14.1876 20.1881 12.8311 20.75 11.4166 20.75H10.0832C8.21658 20.75 7.08325 20.1767 6.08991 19.19L3.68991 16.79C3.46053 16.536 3.33763 16.2035 3.34666 15.8613C3.35568 15.5192 3.49594 15.1936 3.73839 14.952C3.98084 14.7104 4.30691 14.5713 4.64909 14.5635C4.99127 14.5557 5.32335 14.6797 5.57658 14.91L6.74991 16.0833"
                stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M11.4167 0.75L14.0834 3.41667L11.4167 6.08334" stroke="#909090" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round" />
              <path d="M0.75 3.41669H14.0834" stroke="#909090" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M3.41667 0.75L0.75 3.41667L3.41667 6.08334" stroke="#909090" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <div class="mbw-time-list mbw-mobile-overflow">
            <button v-for="time in timeSlotsForSelectedDate" :key="time" type="button" class="mbw-time-pill" :class="{
              'mbw-time-pill--selected': selectedSlot === `${selectedDate} ${time}`,
            }" @click="handleSelectTime(time)">
              {{ time }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <div
      class="mbw-calendar-actions"
      v-if="state !== 'DateChosen' && enabled && !showNoSlotsMessage && !loadingAvailability"
    >
      <button type="button" class="mbw-calendar-next" :title="!selectedSlot ? 'Musisz wybrać datę i godzinę' : ''"
        :disabled="!selectedSlot" @click="emit('toggle-display-mode')">
        Dalej
      </button>
    </div>
  </div>
</template>
